import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we're mid-Google-OAuth callback, skip the /auth/me probe — the
    // AuthCallback page will exchange the session_id and then refresh() us.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const register = async (email, password, name, extras = {}) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name, ...extras });
      // Only set the user (i.e. treat as logged-in) if the server actually
      // gave us a session — auto-verified lifetime-plus accounts skip the
      // email-verification wall. Normal signups need to verify their email
      // before they can log in, so we intentionally do NOT setUser here.
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
        return { ok: true, verified: true };
      } catch {
        // No session cookie yet — account exists but is unverified.
        return { ok: true, verified: false, email: data.email };
      }
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
